import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';
import type { AddressResult, PlaceType } from '../components/map/AddressSearchBar/types';
import type { Coordinates } from '../types';

interface UseAddressSearchReturn {
  results: AddressResult[];
  isLoading: boolean;
  error: string | null;
  searchAddresses: (
    query: string,
    placeTypes?: Set<PlaceType>,
    userLocation?: Coordinates
  ) => Promise<void>;
  clearResults: () => void;
  clearError: () => void;
}

export const useAddressSearch = (): UseAddressSearchReturn => {
  const { t } = useTranslation();
  const [results, setResults] = useState<AddressResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchAddresses = useCallback(async (
    searchQuery: string,
    placeTypes?: Set<PlaceType>,
    userLocation?: Coordinates
  ) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    // Block search if no place types are selected
    if (placeTypes && placeTypes.size === 0) {
      setResults([]);
      setError(t('search.filter.noTypesSelected'));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { searchAddresses: nominatimSearch } = await import('../services/nominatimService');
      const searchResults = await nominatimSearch(searchQuery, 5, undefined, placeTypes, userLocation);

      const convertedResults: AddressResult[] = searchResults.map(result => ({
        id: result.id,
        display_name: result.displayName,
        lat: result.coordinates.lat.toString(),
        lon: result.coordinates.lng.toString(),
        type: result.type,
        importance: result.importance,
        distance: result.distance,
      }));

      setResults(convertedResults);
    } catch (error: unknown) {
      logger.error('Address search failed', 'AddressSearch', { searchQuery, error });
      setError(error instanceof Error ? error.message : t('search.address.error'));
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const clearResults = useCallback(() => {
    setResults([]);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    results,
    isLoading,
    error,
    searchAddresses,
    clearResults,
    clearError,
  };
};